import { useMemo } from 'react';
import type React from 'react';

import { Button, Spinner, Text } from '@change/ds-core-ui';
import { TextField } from '@change/ds-forms';

import { Translate, useI18n } from '@change-corgi/core/react/i18n';

import { useCreateVanityUrlForm } from 'src/app/pages/starterDashboardV2/shared/hooks';

type Props = {
	petitionId: string;
	displayedSignatures: number;
};

export const CreateVanityLinkForm: React.FC<Props> = ({ petitionId, displayedSignatures }) => {
	const { translate, translatePlural } = useI18n();
	const {
		data: { vanityUrl, urlError, wordCount, loading },
		actions: { handleChange, handleSubmit },
	} = useCreateVanityUrlForm({ petitionId, displayedSignatures });

	const validateVanityLink = () => {
		if (urlError && urlError.length > 0) {
			return urlError;
		}
		return null;
	};

	const vanityLinkInputPlaceholder = useMemo(
		() => translate('corgi.petition.starter_dashboard_v2.grow_petition.copy_link.vanity_link_input_placeholder'),
		[translate],
	);
	return (
		// Add a slight 2px padding to prevent the TextField's Focus Ring from overflowing its container
		<form className="w-full p-[2px]" data-qa="create-vanity-link-form" onSubmit={handleSubmit}>
			<div className="flex flex-col gap-6">
				<div className="flex flex-row items-center gap-2">
					<Text size="medium" className="mb-7 leading-5">
						<Translate value="corgi.petition.starter_dashboard_v2.grow_petition.copy_link.domain_prefix" />
					</Text>
					<div className="flex-1">
						<TextField
							name="vanity-url-input"
							type="text"
							label={translate('corgi.petition.starter_dashboard_v2.grow_petition.copy_link.custom_url_label')}
							isRequired
							labelIsVisible={false}
							inputElementType="input"
							validate={validateVanityLink}
							placeholder={vanityLinkInputPlaceholder}
							onChange={handleChange}
							validationBehavior="native"
							supportingText={
								!urlError
									? translatePlural(
											'corgi.petition.starter_dashboard_v2.grow_petition.copy_link.character_count',
											wordCount,
											{
												characterCount: wordCount,
											},
										)
									: null
							}
							value={vanityUrl}
							errorMessage={urlError ? urlError.join(', ') : null}
							isInvalid={urlError ? urlError.length > 0 : false}
							size="large"
						/>
					</div>
				</div>
				<div className="bg-background-overlay rounded-lg p-3">
					<ul className="list-inside list-disc wrap-break-word break-keep">
						<li>
							<Text size="small">
								<Translate value="corgi.petition.starter_dashboard_v2.grow_petition.copy_link.vanity_link_tip_1" />
							</Text>
						</li>
						<li>
							<Text size="small">
								<Translate value="corgi.petition.starter_dashboard_v2.grow_petition.copy_link.vanity_link_tip_2" />
							</Text>
						</li>
						<li>
							<Text size="small">
								<Translate value="corgi.petition.starter_dashboard_v2.grow_petition.copy_link.vanity_link_tip_3" />
							</Text>
						</li>
					</ul>
				</div>
				<div className="mt-2 flex flex-row justify-end">
					<Button
						data-qa="vanity-link-modal-create-button"
						size="small"
						variant="primary"
						type="submit"
						disabled={loading}
						className="w-full md:w-fit"
					>
						{loading ? (
							<Spinner className="mx-7 size-6" />
						) : (
							<Translate value="corgi.petition.starter_dashboard_v2.grow_petition.copy_link.vanity_link_cta" />
						)}
					</Button>
				</div>
			</div>
		</form>
	);
};
